import { usePostHog } from "@posthog/react";
import { useEffect, useRef, useState } from "react";

import {
  claimNoContactExitShown,
  captureNoContactExitClicked,
  captureNoContactExitShown,
} from "../../lib/marketplace-analytics";
import type { NoContactExit } from "../../lib/marketplace-status";
import {
  NO_CONTACT_EXIT_BUTTON,
  noContactExitBody,
  noContactExitHeadline,
} from "../../lib/no-contact-exit-copy";
import { Button } from "../ui/button";
import {
  DisconnectMarketplaceDialog,
  type DisconnectHandler,
} from "./DisconnectMarketplaceDialog";

/**
 * NoContactExitBanner (plan 462 M5) — the connected card's "haven't heard
 * from your agent?" exit.
 *
 * Renders from `noContactExit.eligible` ONLY. The server decides eligibility
 * live on every read, so this never looks at `eligible_since` or any date:
 * the option can disappear on the next load (the agent reached out late) and
 * the client has no clock to disagree with it. The card asks rather than
 * asserts, because Sill cannot see phone calls.
 *
 * "End connection" opens the existing `DisconnectMarketplaceDialog` with
 * `agent_unresponsive` preselected and the unchecked "Put my passport back on
 * the market" choice. The caller's `onDisconnect` should NOT refresh the
 * connection read: that unmounts this banner (and the dialog's done state
 * with it) before the buyer sees the outcome. Refresh from `onEnded`, which
 * fires when the dialog closes after a completed disconnect, or when the
 * banner unmounts after one.
 *
 * Analytics carry `{role}` only. `shown` fires once per connection per
 * session (`claimNoContactExitShown`); `clicked` on the button.
 */
export interface NoContactExitBannerProps {
  connectionId: string;
  /** Pro display name; empty falls back to neutral "your agent" copy. */
  proName: string;
  role: string;
  noContactExit: NoContactExit | undefined;
  onDisconnect: DisconnectHandler;
  /**
   * Called when the dialog closes AFTER a disconnect completed, or when the
   * banner unmounts after one.
   */
  onEnded?: () => void;
}

export function NoContactExitBanner({
  connectionId,
  proName,
  role,
  noContactExit,
  onDisconnect,
  onEnded,
}: NoContactExitBannerProps) {
  const posthog = usePostHog();
  const capture = (event: string, properties?: Record<string, unknown>) => {
    posthog?.capture?.(event, properties);
  };
  const [dialogOpen, setDialogOpen] = useState(false);
  const endedRef = useRef(false);
  const eligible = noContactExit?.eligible === true;

  useEffect(() => {
    if (!eligible) return;
    if (claimNoContactExitShown(connectionId)) {
      captureNoContactExitShown(
        (event, properties) => posthog?.capture?.(event, properties),
        role,
      );
    }
  }, [posthog, connectionId, eligible, role]);

  // A completed disconnect defers the caller's refresh to the dialog's
  // close. If this banner unmounts first, tell the caller now (onClose resets
  // the flag, so it never fires twice).
  const onEndedRef = useRef(onEnded);
  useEffect(() => {
    onEndedRef.current = onEnded;
  });
  useEffect(
    () => () => {
      if (endedRef.current) {
        endedRef.current = false;
        onEndedRef.current?.();
      }
    },
    [],
  );

  if (!eligible) return null;

  const name = proName.trim() || "your agent";

  const handleDisconnect: DisconnectHandler = async (params) => {
    const result = await onDisconnect(params);
    endedRef.current = true;
    return result;
  };

  return (
    <div
      className="marketplace-surface flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5"
      data-testid="no-contact-exit-banner"
    >
      <div className="space-y-1">
        <p
          className="text-sm font-semibold text-foreground"
          data-testid="no-contact-exit-headline"
        >
          {noContactExitHeadline(name)}
        </p>
        <p
          className="text-sm text-muted-foreground"
          data-testid="no-contact-exit-body"
        >
          {noContactExitBody(name)}
        </p>
      </div>
      <Button
        data-testid="no-contact-exit-button"
        onClick={() => {
          captureNoContactExitClicked(capture, role);
          setDialogOpen(true);
        }}
        variant="outline"
      >
        {NO_CONTACT_EXIT_BUTTON}
      </Button>
      <DisconnectMarketplaceDialog
        agentName={name}
        connectionId={connectionId}
        initialReason="agent_unresponsive"
        offerRepublish
        onClose={() => {
          setDialogOpen(false);
          if (endedRef.current) {
            endedRef.current = false;
            onEnded?.();
          }
        }}
        onDisconnect={handleDisconnect}
        open={dialogOpen}
      />
    </div>
  );
}
